import React, { useState, useEffect, useCallback, useContext } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, InputLabel, FormControl, Switch, FormControlLabel, CircularProgress,
  Snackbar, Alert, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { CONTEXTS } from 'figshelf-modules';

const CODE_TYPES = [
  { value: 'premium_activation', label: 'Premium Activation (days)' },
  { value: 'percentage', label: 'Percentage Discount' },
  { value: 'fixed', label: 'Fixed Discount' },
];

const EMPTY = {
  code: '', type: 'premium_activation', discountValue: 30,
  description: '', maxUses: '', expiresAt: '', isActive: true,
};

const CodeFormDialog = ({ open, code, onClose, onSave }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (code) {
      setForm({
        code: code.code || '',
        type: code.type || 'premium_activation',
        discountValue: code.discountValue ?? 30,
        description: code.description || '',
        maxUses: code.maxUses ?? '',
        expiresAt: code.expiresAt ? code.expiresAt.slice(0, 10) : '',
        isActive: code.isActive !== false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [code, open]);

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async () => {
    setSaving(true);
    const payload = {
      ...form,
      discountValue: Number(form.discountValue),
      maxUses: form.maxUses === '' ? null : Number(form.maxUses),
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{code ? 'Edit Code' : 'Create Discount Code'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField label="Code" value={form.code} onChange={f('code')} inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: 2 } }} fullWidth required />
        <FormControl fullWidth>
          <InputLabel>Type</InputLabel>
          <Select value={form.type} label="Type" onChange={f('type')}>
            {CODE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label={form.type === 'premium_activation' ? 'Days of Premium' : form.type === 'percentage' ? 'Percentage (0–100)' : 'Fixed Amount'}
          type="number"
          value={form.discountValue}
          onChange={f('discountValue')}
          fullWidth
        />
        <TextField label="Description (optional)" value={form.description} onChange={f('description')} fullWidth />
        <TextField label="Max Uses (blank = unlimited)" type="number" value={form.maxUses} onChange={f('maxUses')} fullWidth />
        <TextField label="Expires At (optional)" type="date" value={form.expiresAt} onChange={f('expiresAt')} fullWidth InputLabelProps={{ shrink: true }} />
        <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !form.code}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RedemptionsDialog = ({ open, code, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Redemptions — {code?.code}</DialogTitle>
    <DialogContent>
      {!code?.redemptions?.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No redemptions yet.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell>Premium Days</TableCell>
                <TableCell>Redeemed At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {code.redemptions.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.userId}</TableCell>
                  <TableCell>{r.premiumDaysGranted ?? '—'}</TableCell>
                  <TableCell>{new Date(r.redeemedAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>
);

const typeColor = (t) => ({ premium_activation: 'warning', percentage: 'info', fixed: 'default' }[t] ?? 'default');
const typeLabel = (t, v) => ({ premium_activation: `${v}d Premium`, percentage: `${v}% off`, fixed: `$${v} off` }[t] ?? t);

const DiscountCodes = () => {
  const api = useContext(CONTEXTS.APIContext);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCode, setEditCode] = useState(null);
  const [redemptionsCode, setRedemptionsCode] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/discount-codes/admin');
      const raw = res?.data ?? res ?? [];
      setCodes(Array.isArray(raw) ? raw : []);
    } catch { notify('Failed to load codes', 'error'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleSave = async (form) => {
    try {
      if (editCode) {
        await api.patch(`/discount-codes/admin/${editCode.id}`, form);
        notify('Code updated');
      } else {
        await api.post('/discount-codes/admin', form);
        notify('Code created');
      }
      setDialogOpen(false);
      setEditCode(null);
      fetchCodes();
    } catch (err) {
      notify(err?.response?.data?.message || 'Save failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/discount-codes/admin/${id}`);
      notify('Code deleted');
      setDeleteConfirm(null);
      fetchCodes();
    } catch { notify('Delete failed', 'error'); }
  };

  const openRedemptions = async (code) => {
    try {
      const res = await api.get(`/discount-codes/admin/${code.id}`);
      setRedemptionsCode(res?.data ?? res);
    } catch { notify('Failed to load redemptions', 'error'); }
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>Discount Codes</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Create and manage codes for premium access and marketplace discounts
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditCode(null); setDialogOpen(true); }}>
            New Code
          </Button>
        </Box>

        {/* Summary */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Codes', value: codes.length, icon: <LocalOfferIcon /> },
            { label: 'Active', value: codes.filter((c) => c.isActive).length, icon: <LocalOfferIcon color="success" /> },
            { label: 'Total Redeemed', value: codes.reduce((a, c) => a + (c.usedCount || 0), 0), icon: <PeopleIcon /> },
          ].map((s) => (
            <Card key={s.label} sx={{ flex: '1 1 140px', borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {s.icon}
                <Box>
                  <Typography variant="h6" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Type / Value</TableCell>
                      <TableCell>Uses</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No codes yet — click "New Code" to create one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {codes.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>{c.code}</Typography>
                          {c.description && <Typography variant="caption" color="text.secondary">{c.description}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip label={typeLabel(c.type, c.discountValue)} color={typeColor(c.type)} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {c.usedCount}{c.maxUses !== null ? ` / ${c.maxUses}` : ' / ∞'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={c.expiresAt && new Date(c.expiresAt) < new Date() ? 'error' : 'text.primary'}>
                            {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={c.isActive ? 'Active' : 'Inactive'} color={c.isActive ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Redemptions">
                            <IconButton size="small" onClick={() => openRedemptions(c)}><PeopleIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditCode(c); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(c)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <CodeFormDialog open={dialogOpen} code={editCode} onClose={() => { setDialogOpen(false); setEditCode(null); }} onSave={handleSave} />
      <RedemptionsDialog open={!!redemptionsCode} code={redemptionsCode} onClose={() => setRedemptionsCode(null)} />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Code</DialogTitle>
        <DialogContent><Typography>Delete <b>{deleteConfirm?.code}</b>? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm.id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default DiscountCodes;
